import { useEffect, useState } from "react";
import { Button, FormControlLabel, FormHelperText, Grid, MenuItem, Radio, Typography } from "@mui/material";
import { Field, Form, Formik } from "formik";
import { TextField, RadioGroup } from "formik-mui";
import { useTranslation } from "react-i18next";

import { CaseCreationType } from "src/types";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { useCaseCreateSchema } from "src/schemas/case-create.schema";
import { useSrids } from "src/hooks/useSrids";

export interface CaseCreateFormValues {
  name: string;
  description: string;
  userSrid: string;
  caseType: CaseCreationType;
}

interface Props {
  values: CaseCreateFormValues | null;
  onCancel: () => void;
  onSubmit: (values: CaseCreateFormValues) => void;
}

export function CaseCreateForm(props: Props) {
  const { values } = props;

  const { t } = useTranslation();
  const { data: srids } = useSrids();
  const validationSchema = useCaseCreateSchema();

  const [initialValues, setInitialValues] = useState<CaseCreateFormValues>({
    name: values?.name || "",
    description: values?.description || "",
    caseType: values?.caseType || CaseCreationType.PREDEFINED,
    userSrid: values?.userSrid || "",
  });

  /**
   * Effects
   */
  useEffect(setInitialSrid, [srids]);

  /**
   * Functions
   */
  function setInitialSrid() {
    if (srids) {
      setInitialValues((v) => ({ ...v, userSrid: srids[0].toString() }));
    }
  }

  return render();

  function render() {
    if (!srids) return null;

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: CaseCreateFormValues) => {
          props.onSubmit(values);
        }}
        enableReinitialize
      >
        {({ errors, isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h4" color="primary">
                  {t("title.createCase")}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="name"
                  label={t("label.name")}
                  type="text"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="description"
                  label={t("label.description")}
                  multiline
                  rows={5}
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  type="text"
                  name="userSrid"
                  select={true}
                  fullWidth
                  variant="outlined"
                  label={t("label.srid")}
                >
                  {srids.map((srid) => (
                    <MenuItem key={srid} value={srid}>
                      EPSG:{srid}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>

              {/* <Grid item xs={12}>
                <Field component={RadioGroup} name="caseType">
                  <FormControlLabel
                    value={CaseCreationType.PREDEFINED}
                    control={<Radio disabled={isSubmitting} />}
                    label={t<string>("label.usePredefinedData")}
                    disabled={isSubmitting}
                  />
                  <FormControlLabel
                    value={CaseCreationType.NORMAL}
                    control={<Radio disabled={isSubmitting} />}
                    label={t<string>("label.useOwnData")}
                    disabled={isSubmitting || true}
                  />
                </Field>
                <FormHelperText error>{errors.caseType}</FormHelperText>
              </Grid> */}

              <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                <Button variant="text" onClick={props.onCancel}>
                  {t("label.cancel")}
                </Button>
                <Button type="submit" color="primary" variant="contained">
                  {t("label.next")}
                </Button>
              </ButtonGrid>
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }
}
